export const formatNumber = (
  wrap,
  number,
  decimals = true,
  unit = false,
  currency = false
) => {
  let { locale, thousandSymbol, millionSymbol, currencySymbol } = wrap.dataset
  const isEnLocale = locale === "en"

  let value = number
  let suffix

  if (decimals) {
    if (number >= 1000000) {
      value = number / 1000000
      suffix = millionSymbol
    } else if (number >= 1000) {
      value = number / 1000
      suffix = thousandSymbol
    }
  }

  const rounded = Math.round(value * 10) / 10

  const hasDecimal = rounded % 1 !== 0

  let resNumber = new Intl.NumberFormat(isEnLocale ? "en-US" : "cs-CZ", {
    minimumFractionDigits: hasDecimal ? 1 : 0,
    maximumFractionDigits: hasDecimal ? 1 : 0,
  }).format(rounded)

  if (currency && currencySymbol === "€" && isEnLocale) {
    resNumber = `${currencySymbol}${resNumber}`
  } else if (currency) {
    resNumber += ` ${currencySymbol}`
  }

  if (unit) resNumber += ` ${suffix}`

  return resNumber
}

export const verticalLinePlugin = {
  afterDraw: (chart) => {
    const { ctx, tooltip } = chart

    if (!tooltip || tooltip.opacity === 0) return

    const tooltipIndex = tooltip.dataPoints
      ? tooltip.dataPoints[0].dataIndex
      : -1

    if (tooltipIndex === -1) return

    const visibleDatasets = chart.data.datasets.filter((dataset, index) =>
      chart.isDatasetVisible(index)
    )

    let maxYValue = Number.NEGATIVE_INFINITY
    visibleDatasets.forEach((dataset) => {
      const value = dataset.data[tooltipIndex]
      if (value > maxYValue) {
        maxYValue = value
      }
    })

    const yScale = chart.scales.y
    const maxYPos = yScale.getPixelForValue(maxYValue)

    const xPos = tooltip.caretX

    ctx.save()
    ctx.globalCompositeOperation = "destination-over"
    ctx.beginPath()
    ctx.moveTo(xPos, chart.chartArea.bottom)
    ctx.lineTo(xPos, maxYPos)
    ctx.lineWidth = 1
    ctx.strokeStyle = "#CAD7DC"
    ctx.stroke()
    ctx.restore()
  },
}

export const axisLabelPlugin = {
  id: "customYAxisLabel",
  beforeInit(chart) {
    const opts = chart.options.layout || {}
    chart.options.layout = {
      ...opts,
      padding: {
        ...opts.padding,
        right: Math.max(opts.padding?.right || 0, isSmallScreen() ? 10 : 20),
      },
    }
  },
  afterDraw(chart) {
    let { locale, millionSymbol, thousandSymbol, currencySymbol } =
      chart.canvas.dataset

    currencySymbol =
      locale !== "en" && currencySymbol === "€" ? "EUR" : currencySymbol

    const { ctx, chartArea, scales } = chart
    const yAxis = scales.y

    ctx.save()
    ctx.font = `${isSmallScreen() ? "10px" : "14px"} Gotham, sans-serif`
    ctx.fillStyle = "#a9b3b6"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    const x = yAxis.right + (isSmallScreen() ? 5 : 10)
    const y = chartArea.bottom - 80

    const maxYValue = chart.scales.y.max

    let label = ""

    const isOverThousand = maxYValue >= 1000
    const isOverMillion = maxYValue >= 1000000

    if (isOverThousand) label = thousandSymbol
    if (isOverMillion) label = millionSymbol

    ctx.translate(x, y)
    ctx.rotate(-Math.PI / 2)
    ctx.fillText(`${label} ${currencySymbol}`, 0, 0)
    ctx.restore()
  },
}

export const customTickOffsetPlugin = {
  id: "customTickOffset",
  beforeInit(chart) {
    chart.options.layout ??= {}
    chart.options.layout.padding ??= {}

    const current = chart.options.layout.padding

    chart.options.layout.padding = {
      ...current,
      bottom: Math.max(current.bottom || 0, isSmallScreen() ? 25 : 20),
    }
  },
  afterDraw(chart) {
    const canvas = chart.canvas
    const ctx = chart.ctx
    const scale = chart.scales.x
    const ticks = scale.ticks
    const total = ticks.length

    const wrapper = canvas.parentNode
    const yearStr = wrapper.getAttribute("data-years") || ""
    const years = yearStr.split(",").filter((y) => y.trim() !== "")

    const baseLimit = years.length || total
    const maxTicks = isSmallScreen() ? Math.min(baseLimit, 8) : baseLimit

    const step = Math.ceil(total / maxTicks)

    ctx.save()
    ctx.font = "12px Gotham, sans-serif"
    ctx.fillStyle = "#6A6D6F"
    ctx.textAlign = "center"
    ctx.textBaseline = "top"

    scale.ticks.forEach((tick, index) => {
      if (index % step !== 0 && index !== 0 && index !== total - 1) return

      const x = scale.getPixelForTick(index)
      const y = scale.bottom + 10
      ctx.fillText(tick.label, x, y)
    })

    ctx.restore()
  },
}

export const isSmallScreen = () => window.innerWidth < 1280
